import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';

import { NEUTROS as T } from '../../../utils/micrositioTema';
import { clickInterno } from '../../../utils/navegacionSpa';
import { fixUrl } from '../../../services/api';

// A NIVEL DE MÓDULO, como `OpcionPlantilla` en `EstructuraCatalogoTab` y
// `AnimalCard` en `CatalogoPage`: definido adentro del render, cada click en
// un chip —que cambia el estado del padre— lo convertía en un componente
// NUEVO para React, que desmontaba y volvía a montar todos los botones. El
// tocado perdía el foco y la transición de color nunca llegaba a verse. Lo
// encontró el review del commit (2026-10-07).
//
// Un chip por opción; el activo se pinta con el acento y se destilda al
// volver a tocarlo.
const Chip = ({ activo, acento, fuente, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`pj-op2-chip${activo ? ' pj-op2-chip-activo' : ''}`}
    style={{
      fontFamily: fuente,
      color: activo ? '#fff' : acento,
      background: activo ? acento : `${acento}14`,
    }}
  >
    {children}
  </button>
);

/**
 * La sección de HEMBRAS en la plantilla OP-2: un mosaico a dos columnas, a
 * ancho completo, donde la PRIMERA celda es la cabecera —título, bajada y
 * filtros— y cada celda siguiente es una hembra con su foto de fondo y una
 * barra con el nombre, la categoría y la raza.
 *
 * LA DIFERENCIA CON OP-1 ES LA DISPOSICIÓN, NO EL CONTENIDO, igual que con
 * los toros: OP-1 pone las hembras en una grilla de tarjetas dentro de los
 * 1280px; OP-2 las lleva al borde de la pantalla, la foto es la celda entera
 * y la cabecera ocupa el lugar de la primera foto. Son las piezas 2 y 3 de
 * OP-2 del wireframe (`assets/plantillas/op-2.jpg`), construidas el
 * 2026-10-07 sobre el tenant demo, que es el primero con hembras preparadas.
 *
 * LOS FILTROS FILTRAN LO QUE HAY EN LA PORTADA —las destacadas—, al instante
 * y sin recargar: el wireframe dice "filtros ocultar/mostrar contenido". Los
 * chips salen de las razas y categorías que REALMENTE hay entre ellas, así
 * nunca hay un chip que no muestre nada. Se prenden y apagan con
 * `hembras_filtros_on`, el switch del panel que hasta hoy no hacía nada. El
 * listado completo sigue detrás de "Ver todas", igual en las dos plantillas.
 *
 * NO decide qué páginas existen: el "ver todas" y la ficha son los mismos.
 *
 * @param {ReactNode} cabecera       Título y bajada, armados por `SeccionAnimales`.
 * @param {ReactNode} pie            El botón "Ver todas", centrado debajo. `null` si no hay más.
 * @param {boolean}   filtrosOn      `config.hembras_filtros_on !== false`.
 */
const MosaicoHembrasOp2 = ({ datos, config, tipografia, slug, cabecera, pie, filtrosOn }) => {
  const navigate = useNavigate();
  const acento = config.color_primary || '#1a5f2a';
  const colorTitulo = config.color_title || '#1a1a1a';
  const [raza, setRaza] = useState(null);
  const [categoria, setCategoria] = useState(null);

  // `datos.destacados` directo en las deps y no un `|| []` intermedio: ese
  // array nuevo en cada render invalidaría los dos memos de abajo siempre.
  const hembras = datos.destacados;

  // Las opciones salen de las hembras presentes y no de un catálogo de razas:
  // un chip "Angus" en una portada sin Angus es una promesa vacía. Un grupo
  // con una sola opción no se dibuja: filtrar entre una cosa no filtra.
  const razas = useMemo(
    () => [...new Set((hembras || []).map((h) => h.nom_raza).filter(Boolean))].sort(),
    [hembras],
  );
  const categorias = useMemo(
    () => [...new Set((hembras || []).map((h) => h.nom_categoria).filter(Boolean))].sort(),
    [hembras],
  );
  const hayFiltros = filtrosOn && (razas.length > 1 || categorias.length > 1);

  const visibles = (hembras || []).filter(
    (h) => (!raza || h.nom_raza === raza) && (!categoria || h.nom_categoria === categoria),
  );
  const fuente = tipografia.body;

  return (
    <>
      <div className="pj-op2-mosaico">
        {/* LA PRIMERA CELDA ES LA CABECERA. Ocupa el lugar de una foto a
            propósito: en el wireframe el título no va arriba de la grilla
            sino adentro, como una pieza más del mosaico. */}
        <div className="pj-op2-hembra-cabecera">
          {cabecera}
          {hayFiltros && (
            <div className="pj-op2-filtros" style={{ fontFamily: tipografia.body }}>
              {razas.length > 1 && (
                <div className="pj-op2-filtro-grupo">
                  {razas.map((r) => (
                    <Chip
                      key={r}
                      activo={raza === r}
                      acento={acento}
                      fuente={fuente}
                      onClick={() => setRaza(raza === r ? null : r)}
                    >
                      {r}
                    </Chip>
                  ))}
                </div>
              )}
              {categorias.length > 1 && (
                <div className="pj-op2-filtro-grupo">
                  {categorias.map((c) => (
                    <Chip
                      key={c}
                      activo={categoria === c}
                      acento={acento}
                      fuente={fuente}
                      onClick={() => setCategoria(categoria === c ? null : c)}
                    >
                      {c}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {visibles.map((animal) => {
          const rutaFicha = `/genetica/${slug}/animal/${animal.slug}`;
          const foco = animal.imagen_foco || {};
          return (
            /* LA CELDA ENTERA ES EL LINK A LA FICHA, y la foto va de fondo y no
               como <img> con visor, por la misma decisión que en la banda de
               toros: en OP-2 la foto no se amplía desde el index; el que quiere
               ver de cerca tiene la ficha a un click. El `href` sigue
               sirviendo a ctrl+click y a los buscadores (ver `navegacionSpa`). */
            <a
              key={animal.id_animal}
              href={rutaFicha}
              onClick={clickInterno(navigate, rutaFicha)}
              className="pj-op2-hembra"
            >
              {animal.imagen && (
                <div
                  className="pj-op2-hembra-foto"
                  role="presentation"
                  style={{
                    backgroundImage: `url("${encodeURI(fixUrl(animal.imagen))}")`,
                    backgroundPosition: `${foco.x ?? 50}% ${foco.y ?? 50}%`,
                  }}
                />
              )}
              <div className="pj-op2-hembra-pie">
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15, fontWeight: 800, letterSpacing: '-.005em', lineHeight: 1.25,
                      fontFamily: tipografia.title, color: colorTitulo,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {animal.nombre_corto || animal.nom_animal || animal.rp_animal || 'Sin nombre'}
                  </div>
                  <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 2, fontWeight: 500 }}>
                    {[animal.nom_categoria, animal.nom_raza].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <span
                  style={{
                    flex: 'none', fontSize: 12.5, fontWeight: 700, color: acento,
                    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                  }}
                >
                  Ver ficha <RightOutlined style={{ fontSize: 10 }} />
                </span>
              </div>
            </a>
          );
        })}

        {/* FILTRO SIN RESULTADOS entre las destacadas: se dice, y se ofrece el
            listado completo, donde sí puede haber. Una celda vacía en
            silencio se lee como que la sección se rompió. El link va SIEMPRE,
            aunque no haya "más" —el listado existe igual—, porque la pregunta
            del comprador acá es "¿hay alguna Brangus?" y la respuesta está
            del otro lado. */}
        {visibles.length === 0 && (
          <div className="pj-op2-hembra-cabecera">
            <div style={{ fontFamily: tipografia.body, color: T.textDim, fontSize: 15 }}>
              No hay hembras {[raza, categoria].filter(Boolean).join(' · ')} entre las destacadas.
            </div>
            <a
              href={`/genetica/${slug}/hembras`}
              onClick={clickInterno(navigate, `/genetica/${slug}/hembras`)}
              style={{
                marginTop: 12, fontFamily: tipografia.body, fontSize: 13.5, fontWeight: 700,
                color: acento, display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              Buscar en todas las hembras <RightOutlined style={{ fontSize: 10 }} />
            </a>
          </div>
        )}
      </div>

      {pie && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 24px 0' }}>
          {pie}
        </div>
      )}
    </>
  );
};

export default MosaicoHembrasOp2;

/* SIN COMILLAS INVERSAS en estos comentarios: viven dentro de un template
   literal de JS y la primera cierra la cadena. */
export const ESTILOS_MOSAICO_HEMBRAS = `
/* EL MOSAICO: dos columnas iguales, sin separacion, al borde de la pantalla
   hasta 1600px y centrado mas alla (en un monitor 4K dos fotos de 1900px de
   ancho no son una vidriera, son un muro). Las fotos comparten alto por
   proporcion (3:2) y no por un alto fijo: en una pantalla ancha crecen y en
   una angosta se achican.

   LA CABECERA NO LLEVA PROPORCION: mide lo que mide su contenido, y la fila
   de la grilla toma el mayor de los dos. Con la proporcion puesta tambien en
   ella, entre 760 y ~1100px el titulo, la bajada y dos filas de chips
   superaban los ~330px de la celda y se cortaban en silencio (overflow
   hidden). La foto de al lado se estira a la altura de la fila, asi que el
   mosaico sigue leyendose como una pieza. */
.pj-op2-mosaico {
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 1600px;
  margin: 0 auto;
}
.pj-op2-mosaico > * {
  position: relative;
  min-height: 300px;
}
.pj-op2-hembra {
  aspect-ratio: 3 / 2;
  overflow: hidden;
}
/* Las celdas alternan dos grises apenas distintos, como el wireframe, para
   que una hembra sin foto no sea un hueco blanco entre dos fotos. */
.pj-op2-mosaico > *:nth-child(4n+2),
.pj-op2-mosaico > *:nth-child(4n+3) { background: ${T.pearl}; }
.pj-op2-mosaico > *:nth-child(4n+1),
.pj-op2-mosaico > *:nth-child(4n+4) { background: #e9e9e7; }

.pj-op2-hembra-cabecera {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px clamp(24px, 6vw, 80px);
}
.pj-op2-filtros {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 24px;
}
.pj-op2-filtro-grupo {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
/* El MISMO chip de raza de las tarjetas y la ficha —radio 4, sin borde, fondo
   tenue del acento— pero como boton: el activo se rellena. */
.pj-op2-chip {
  border: 0;
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: .03em;
  cursor: pointer;
  transition: background .15s, color .15s;
}

.pj-op2-hembra {
  display: block;
  color: inherit;
  text-decoration: none;
}
.pj-op2-hembra-foto {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-repeat: no-repeat;
  transition: transform .4s ease;
}
.pj-op2-hembra:hover .pj-op2-hembra-foto { transform: scale(1.03); }
/* La barra del nombre, apoyada abajo DENTRO de la celda y no debajo de la
   foto: la foto es la celda entera. Blanca casi opaca para leerse sobre
   cualquier foto, con aire a los lados para que no toque el borde. */
.pj-op2-hembra-pie {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255,255,255,.94);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(35,35,35,.14);
}

@media (max-width: 760px) {
  .pj-op2-mosaico { grid-template-columns: 1fr; }
  .pj-op2-mosaico > * { min-height: 0; }
  .pj-op2-hembra { aspect-ratio: 4 / 3; }
  .pj-op2-hembra-cabecera { padding: 40px 24px; }
  .pj-op2-hembra-pie { left: 12px; right: 12px; bottom: 12px; padding: 10px 12px; }
}
`;
